import { uuid } from "@xapp/chat-widget-core";

import { Request, Response } from "stentor-models";

import { setAccountStatus, setConnectionStatus } from "../store/actions";
import { ACCOUNT_STATUS_TYPE, ActionType, CONNECTION_STATUS_TYPE } from "../store/ChatAction";
import { err, log } from "../utils";
import { ChatServer, ChatServerOptions, OfflineMessage, VisitorInfoMessage } from "./ChatServer";
import { ChatMessageRequest, ChatServerMessage } from "./ChatServerMessage";
import { requestFromMessage } from "../utils/requestFromMessage";
import { responseToMessage } from "../utils/responseToMessage";
import { requestToMessage } from "../utils/requestToMessage";
import { ConfigurableMessagesItems, getConfigurableMessagesConfig } from "../utils/configurableMessages";
import setSessionId from "../store/actions/setSessionId";

export interface StentorRouterChatConfig {
    readonly url: string;
}

export interface FailureData {
    readonly type: "BOT";
    readonly tries: number;
    readonly delay: number;
    readonly error: string;
}

// the internal router message format. depends on direction.
export type MessageData = Request | Response | ConnectionStatus | FailureData | undefined;

export interface ConnectionStatus {
    sessionCreated: boolean;
    errorMessage?: string;
}

export type EventType =
    | "account status"
    | "connection update"
    | "new message"
    | "user joined"
    | "user left"
    | "typing"
    | "stop typing"
    | "disconnect"
    | "reconnect"
    | "reconnect failed"
    | "reconnect error"
    | "barge in"
    | "barge out"
    | "failure"
    ;

// Mirrors the router side
export interface RouterMessage {
    event: EventType;
    data: MessageData;
    sender: VisitorInfo;
    sessionId: string;
    timeMs: number;
}

export interface VisitorInfo {
    deviceId: "Widget" | "Bot" | "Router";

    userId: string;

    isAdmin: boolean;

    avatarPath?: string;
    displayName?: string;
    email?: string;

    urlAttributes: {
        path?: string[];
        query?: { [name: string]: string };
    };
}

export type HandlerStore = {
    [event in EventType]: (data: MessageData, sender: VisitorInfo, ts: number) => void;
};

export const ROUTER_USER = "router";

const serverInfo: VisitorInfo = {
    deviceId: "Router",
    userId: ROUTER_USER,
    isAdmin: false,
    displayName: "XAPP Server",
    urlAttributes: {
        path: [],
        query: {}
    }
}

export class StentorRouterChat implements ChatServer {
    private dispatch!: (action: ActionType) => void;

    private isDisposed = false;

    private _userId = "";
    private _sessionId = "";

    private visitorInfo!: VisitorInfo;

    private accessToken = "";
    private attributes: Record<string, unknown> = {};

    private isNewSession: boolean;
    private isAdmin = false;
    private urlAttributes: {
        path?: string[];
        query?: { [name: string]: string };
    } = {};

    private readonly config: StentorRouterChatConfig;
    private readonly options: ChatServerOptions | undefined;

    private readonly configurableMessages: ConfigurableMessagesItems[];

    private serverUrl: string;
    private ws: WebSocket;
    private handlers: HandlerStore = {} as HandlerStore;
    private sessionCreated: boolean;
    private appId: string;

    private noOfServerErrors: number;

    constructor(config: StentorRouterChatConfig, options?: ChatServerOptions) {
        this.config = config;
        this.options = options;

        // Dig out the path parameters. Put them into the attributes.
        // The WS url is the barebone domain.

        const url = new URL(this.config.url);

        this.appId = url.searchParams.get("appId");

        const locationUrl = new URL(window.location.href);
        this.isAdmin = locationUrl.searchParams.get("isAdmin") === "true";

        if (this.appId) {
            // New Path if we have the appId on the query string
            this.serverUrl = this.config.url;
            this.urlAttributes.path = url.pathname?.split("/");
            // keep the server url
        } else {
            // Old path
            this.serverUrl = `${url.origin}`;
            const urlPath: string[] = url.pathname?.split("/");
            if (urlPath && urlPath.length > 1) {
                this.serverUrl = `${this.serverUrl}/${urlPath[1]}`;
                this.urlAttributes.path = urlPath.slice(1);
            }
        }

        log(`WS url: ${this.serverUrl} isAdmin: ${this.isAdmin} attributes: ${JSON.stringify(this.attributes)} appId: ${this.appId}`);

        this.configurableMessages = getConfigurableMessagesConfig(options.configurableMessages);

        this.noOfServerErrors = 0;
    }

    public init(dispatch: (action: ActionType) => void): void {
        this.dispatch = dispatch;

        this.setAccountStatus("offline");

        // Register "handlers" for the incoming events

        this.handlers["connection update"] = (data: ConnectionStatus, _sender: VisitorInfo, _ts: number) => {
            log(`Connection update received. Session created: ${data.sessionCreated} Error: ${data.errorMessage || ""}`);
            if (data.sessionCreated) {
                this.sessionCreated = true;
                // This is overkill. But if we are stuck in pending this can help.
                this.setConnectionStatus("online");
            } else {
                // Shut down
                err(`SERVER: something is wrong. Going offline.`)
                this.dispatch(setAccountStatus("offline"));
                this.dispatch(setConnectionStatus("offline"));
            }
        };

        // Server ping (not sent peroidically - at least not for now)
        this.handlers["account status"] = (_data: MessageData, _sender: VisitorInfo, ts: number) => {
            dispatch({
                type: "account_status",
                detail: {
                    status: "online",
                    timestamp: ts || new Date().getTime(),
                },
            });
        };

        this.handlers["failure"] = (data: FailureData, sender: VisitorInfo, _ts: number) => {
            err(`SERVER: something's failed: type: ${data.type} tries: ${data.tries} error: ${data.error}`);
            const { retry, delay, text } = this.configurableMessages[data.tries];
            this.sendFailureMessage(retry, data.delay || delay, text, sender);
        };

        this.handlers["new message"] = (data: MessageData, sender: VisitorInfo, ts: number): void => {
            // Because the router's internal message format is Stentor channel compatible.
            // So the data is either a stentor Request (from widget) or a stentor Response (from bot)

            let message: ChatMessageRequest;

            if (sender.deviceId === "Bot") {
                if (typeof this.options.hooks?.onResponse === "function") {
                    this.options.hooks.onResponse(message as Response);
                }

                message = responseToMessage(data as Response);
            } else if (sender.deviceId === "Widget") {
                message = requestToMessage(data as Request);
            } else {
                err(`Unknown sender device Id: ${sender.deviceId}`);
            }

            this.dispatch({
                type: "chat",
                detail: {
                    type: "chat.msg",
                    user: {
                        displayName: sender.displayName || sender.email || sender.userId,
                        nick: senderToNick(sender),
                        avatarPath: sender.avatarPath
                    },
                    msg: message.msg,
                    timestamp: ts || new Date().getTime(),
                },
            });
        };

        this.handlers["user joined"] = (_data: MessageData, sender: VisitorInfo, ts: number): void => {
            this.dispatch({
                type: "chat",
                detail: {
                    type: "chat.memberjoin",
                    user: {
                        avatarPath: this.options?.bot?.avatarPath,
                        displayName: sender.displayName,
                        nick: senderToNick(sender),
                    },
                    timestamp: ts || new Date().getTime(),
                },
            });
        };

        this.handlers["user left"] = (_data: MessageData, sender: VisitorInfo, ts: number) => {
            this.dispatch({
                type: "chat",
                detail: {
                    type: "chat.memberleave",
                    user: {
                        displayName: sender.displayName,
                        nick: senderToNick(sender),
                    },
                    timestamp: ts || new Date().getTime(),
                },
            });
        };

        this.handlers["typing"] = (_data: MessageData, sender: VisitorInfo, ts: number) => {
            this.dispatch({
                type: "chat",
                detail: {
                    type: "chat.typing",
                    user: {
                        nick: senderToNick(sender),
                    },
                    typing: true,
                    timestamp: ts || new Date().getTime(),
                },
            });
        };

        this.handlers["stop typing"] = (_data: MessageData, sender: VisitorInfo, ts: number) => {
            this.dispatch({
                type: "chat",
                detail: {
                    type: "chat.typing",
                    user: {
                        nick: senderToNick(sender),
                    },
                    typing: false,
                    timestamp: ts || new Date().getTime(),
                },
            });
        };

        this.handlers["disconnect"] = () => {
            err(`SERVER: requested disconnect`);
            this.dispatch(setAccountStatus("offline"));
            this.dispatch(setConnectionStatus("offline"));
        };

        this.handlers["reconnect"] = () => {
            this.dispatch(setAccountStatus("online"));
            this.dispatch(setConnectionStatus("online"));
        };

        this.handlers["reconnect failed"] = (_data: Record<string, unknown>) => {
            err(`SERVER: says re-connect failed`);
            this.dispatch(setAccountStatus("offline"));
            this.dispatch(setConnectionStatus("offline"));
        };

        this.handlers["reconnect error"] = (_data: Record<string, unknown>) => {
            err(`SERVER: says there was an error while re-connecting`);
            this.dispatch(setAccountStatus("offline"));
        };

        // Register the router as a pseudo-agent (serverInfo) so it can send messages (like internal errors)
        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.memberjoin",
                user: {
                    avatarPath: this.options?.bot?.avatarPath,
                    displayName: serverInfo.displayName,
                    nick: senderToNick(serverInfo),
                },
                timestamp: new Date().getTime(),
            },
        });

        // pretend we are online. The emit() will connect
        this.setAccountStatus("online");
        this.setConnectionStatus("online");
    }

    private autoReconnect(wsCreate: { (): WebSocket }) {
        if (this.noOfServerErrors === 0) {
            this.setConnectionStatus("pending");
        }

        const setActStatus = this.setAccountStatus.bind(this);
        const setConnStatus = this.setConnectionStatus.bind(this);

        function startReconnecting() {
            const interval = setInterval(() => {
                const ws = wsCreate();

                if (!ws) {
                    clearInterval(interval);
                    return;
                }

                log("Re-created WS connection");

                ws.onopen = () => {
                    log("Re-opened WS connection");

                    // We are connected
                    setActStatus("online");
                    setConnStatus("online");

                    ws.onclose = () => {
                        log("Closed WS connection (onclosed was called)");
                        startReconnecting();
                    }

                    clearInterval(interval);
                };
            }, 5000);
        }

        log("Creating WS connection");
        const ws = wsCreate();

        if (!ws) {
            return;
        }

        ws.onclose = () => {
            log("Closed WS connection (onclosed was called)");
            startReconnecting();
        }

        this.ws.onopen = () => {
            log("Opened WS connection (onopen was called)");

            // We are connected
            this.setAccountStatus("online");
            this.setConnectionStatus("online");
        };
    }

    private wsClose() {
        if (this.ws) {
            this.ws.onclose = null;
            this.ws.onerror = null;
            this.ws.onopen = null;
            this.ws.onmessage = null;
            this.ws.close();
        }
    }

    private wsCreate(createWsReason?: string): WebSocket {
        if (this.isDisposed) {
            log(`Stopping reconnect for disposed router connector`);
            return undefined;
        } else {
            log(`Trying to re-connect. Reason: "${createWsReason}"`);
        }

        const url = new URL(this.serverUrl);
        url.searchParams.set("userId", this._userId);
        url.searchParams.set("isAdmin", `${this.isAdmin}`);
        url.searchParams.set("createWsReason", createWsReason); // tell the server the reconnect reason

        // Force close - just in case - when you create new
        if (this.ws && !(this.ws.readyState === WebSocket.CLOSED || this.ws.readyState === WebSocket.CLOSING)) {
            const wsState = this.ws.readyState;
            log(`Forced WS close. State: ${wsState}`);
            try {
                this.wsClose();
            } catch (err) {
                err(`Failed to close WS (ready state: ${wsState}): ${err}`);
            }
        }

        this.ws = new WebSocket(url.toString());

        this.ws.onerror = (ev: Event) => {
            err(`Error in WS connection. Url: ${url.toString()} Type: ${!ev ? "?" : ev.type} Ready State: ${this.ws?.readyState}`);
        };

        this.ws.onmessage = (me) => {
            log(`ROUTER says: ${me.data}`);
            const message: RouterMessage = JSON.parse(me.data as unknown as string);
            const handler = this.handlers[message.event];
            if (handler) {
                this.handlers[message.event](message.data, message.sender, message.timeMs);
            } else {
                err(`Unknown router message event: ${message.event}`);
            }
        };

        return this.ws;
    }

    private checkConnection(): boolean {
        return this.ws?.readyState === WebSocket.OPEN;
    }

    private async emit(event: EventType, data?: MessageData): Promise<void> {
        const payloadData = JSON.stringify({
            event,
            data,
            sender: this.visitorInfo,
            sessionId: this._sessionId,
            timeMs: new Date().getTime()
        });

        log(`Widget says: ${payloadData}`);

        if (!this.ws) {
            // Fire up the WebSocket
            this.autoReconnect(this.wsCreate.bind(this, "emitNoWs"));
        }

        await waitFor(this.checkConnection.bind(this), 1000, 10000, "connection")
            .then((t: number) => {
                if (t > 0) {
                    log(`Connection is ready in ${t} ms`);
                }
                this.ws.send(payloadData);
                this.noOfServerErrors = 0;
            })
            .catch((t) => {
                err(`Connection wait timed out in ${t} ms (show error)`);
                this.setConnectionStatus("online");
            });

    }

    private setAccountStatus(status: ACCOUNT_STATUS_TYPE) {
        log(`SERVER account_status: ${JSON.stringify(status)}`);
        this.dispatch(setAccountStatus(status));
    }

    private setConnectionStatus(status: CONNECTION_STATUS_TYPE) {
        log(`SERVER: connection_update: ${JSON.stringify(status)}`);
        this.dispatch(setConnectionStatus(status));
    }

    public sendOfflineMsg(_: OfflineMessage, cb: (error?: Error) => void): void {
        cb();
    }

    public async sendChatMsg(message: ChatServerMessage, cb: (err?: Error) => void): Promise<void> {
        return this.sendChatMsgRequest(
            {
                msg: message,
                timestamp: new Date().getTime(),
                agent: false,
                user: undefined,
                type: "msg",
            },
            cb
        );
    }

    private sendFailureMessage(retry: number, delay: number, text: string, sender: VisitorInfo) {
        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.typing",
                user: {
                    nick: senderToNick(sender),
                },
                typing: false,
                timestamp: +new Date(),
            },
        });

        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.failureMsg",
                user: {
                    nick: senderToNick(sender)
                },
                failureMsg: { retry, delay, text },
                timestamp: +new Date()
            }
        });
    }

    private sendDisconnectMessage() {
        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.msg",
                user: {
                    displayName: "Server",
                    nick: senderToNick(serverInfo),
                    avatarPath: serverInfo.avatarPath
                },
                msg: { text: this.noOfServerErrors === 0 ? 
                    "I cannot connect to the server. Please try later." :
                    "Nope. Still no luck. I still cannot connect to the server. Please don't give up."},
                timestamp: new Date().getTime(),
            }
        });

        this.noOfServerErrors++;
    }

    private checkSession(): boolean {
        return this.sessionCreated;
    }

    public async sendChatMsgRequest(serviceRequest: ChatMessageRequest, cb: (err?: Error) => void): Promise<void> {
        // This is rare, but is happened: no visitor info exchange, but the connection is back.
        // if (this.checkConnection() && !this.checkSession()) {
        //     await this.postVisitorInfo();
        // }

        if (!this.checkSession()) {
            this.setConnectionStatus("pending");
        }

        await waitFor(this.checkSession.bind(this), 1000, 10000, "session creation")
            .then((t: number) => {
                if (t > 0) {
                    log(`Session creation is ready in ${t} ms`);
                }
                this.postMessage(serviceRequest);
            })
            .catch((t) => {
                err(`Session creation wait timed out in ${t} ms`);
                this.setConnectionStatus("online"); // stop pulsing
                this.sendDisconnectMessage();
            });

        this.setConnectionStatus("online");

        cb();
    }

    public sendTyping(_isTyping: boolean): void {
        // TODO: Is this too much traffic?
        // this.emit(isTyping ? "typing" : "stop typing");
    }

    public setVisitorInfo(
        visitorInfoMessage: VisitorInfoMessage,
        sessionId: string,
        cb: (error?: Error) => void
    ): void {
        log(`Visitor Info Message = ${JSON.stringify(visitorInfoMessage)}`);

        this.visitorInfo = {
            deviceId: "Widget",
            userId: visitorInfoMessage.userId,
            displayName: visitorInfoMessage.displayName || visitorInfoMessage.name,
            email: visitorInfoMessage.email,
            isAdmin: this.isAdmin,
            urlAttributes: this.urlAttributes,
        };

        this.attributes = {
            ...visitorInfoMessage.attributes,
            ...this.attributes,
            currentUrl: window.location.href,
        };
        this.accessToken = visitorInfoMessage.accessToken;

        this.startSession(sessionId);

        this.postVisitorInfo(); // we, the widget, joined the server

        cb();
    }

    public sendChatRating(): void { }

    public sendFile(_: File, cb: (err?: Error) => void): void {
        cb();
    }

    public markAsRead(): void {
        // mark the messages read
    }

    public flush(): void { }

    public dispose(): void {
        this.isDisposed = true; 
        if (this.ws) {
            this.wsClose();
        }
        log("Closed web socket (dispose)");
    }

    public sleep(): void {
        this.isDisposed = true; 

        if (this.ws) {
            this.wsClose();
        }
        log("Closed web socket (sleep)");
    }

    public wakeup(): void {
        this.isDisposed = false; 
        
        // wait for the session setup
        if (!this._userId) {
            return;
        }

        // Fire up the WebSocket 
        this.autoReconnect(this.wsCreate.bind(this, "wakeup"));
        log("Waking up");
    }

    public async bargeOut(_cb: (err?: Error) => void): Promise<void> {
        this.emit("barge out");
    }

    public async bargeIn(agentName: string, _cb: (err?: Error) => void): Promise<void> {
        this.visitorInfo.displayName = agentName;
        this.emit("barge in");
    }

    private async postVisitorInfo(): Promise<void> {
        this.sessionCreated = false;
        this.emit("user joined");
    }

    private async postMessage(message: ChatMessageRequest): Promise<void> {
        const userId: string = this._userId;
        const sessionId: string = this._sessionId;
        const accessToken: string = this.accessToken;
        const attributes: Record<string, unknown> = this.attributes || {};

        // First translate the request to bot dialect
        const request = requestFromMessage(
            message,
            userId,
            this.isNewSession,
            sessionId,
            accessToken,
            attributes,
            this.visitorInfo
        );

        this.emit("new message", request);
    }

    private startSession(sessionId: string): void {
        if (this.visitorInfo.userId) {
            this._userId = `${this.visitorInfo.userId}`;
        } else if (this.visitorInfo.email) {
            this._userId = `stentor-widget-user-${this.visitorInfo.email}`;
        } else {
            this._userId = `stentor-widget-user-${uuid()}`;
        }

        const joinSessionId = new URL(window.location.href).searchParams.get("sessionId");

        if (joinSessionId) {
            this._sessionId = joinSessionId;
            this.dispatch(setSessionId(this._sessionId));
            log(`Using enforced session id: ${this._sessionId}`);
        } else if (sessionId) {
            this._sessionId = sessionId;
            log(`Using persisted session id: ${this._sessionId}`);
        } else {
            this._sessionId = `stentor-widget-session-${uuid()}`;
            this.dispatch(setSessionId(this._sessionId));
            log(`Using generated session id: ${this._sessionId}`);
        }

        // Always. This triggers a LAUNCH_REQUEST most of the time
        this.isNewSession = true;

        log(`Started session: ${this._sessionId}`);
    }
}

function senderToNick(sender: VisitorInfo): string {
    // Both are agents
    return sender.deviceId === "Bot" ?
        `agent:bot:${sender.userId}` : `agent:widget:${sender.userId}`;
}

/**
 * 
 * @param check Function that return true/false if the resource is available
 * @param stepMs check period in ms
 * @param timeoutMs max wait in ms
 * @param resource optional resource name. you will see a log message for every check (step)
 * 
 * @returns the elapsed time in ms (both resolve/reject)
 */
export function waitFor(check: { (): boolean }, stepMs: number, timeoutMs: number, resource?: string) {
    let elapsedMs = 0;

    const poll = (resolve: (value: unknown) => void, reject: (value: unknown) => void) => {
        if (elapsedMs >= timeoutMs) {
            reject(elapsedMs);
        } else {
            if (check()) {
                resolve(elapsedMs);
            } else {
                elapsedMs += stepMs;
                if (resource) {
                    log(`Still waiting for ${resource} (${elapsedMs} ms)`);
                }
                setTimeout((_) => poll(resolve, reject), stepMs);
            }
        }
    };

    return new Promise(poll);
}
